import { get } from 'lodash';

/**
 * Calculate add-on price based on add-on name and plan
 * @param {String} addOnName - Name of the add-on
 * @param {String} plan - Plan name
 * @returns {Number} Add-on price
 */
const calculateAddOnPrice = (addOnName, plan) => {
  // Pricing data from linedetailsformlogic.png
  const addOnPricing = {
    'CAN / US Calling - 150 minutes': {
      'Office Phone Basic': 5,
      'Office Phone': 0, // Included
      'Office Phone Plus': 0 // Not available (-)
    },
    'CAN / US Calling - Unlimited': {
      'Office Phone Basic': 15,
      'Office Phone': 15,
      'Office Phone Plus': 0 // Included
    },
    '500 International Minutes': {
      'Office Phone Basic': 10,
      'Office Phone': 10,
      'Office Phone Plus': 10
    }
  };

  return get(addOnPricing, [addOnName, plan], 0);
};

/**
 * Get number of lines, defaulting to 1 if not specified or invalid
 * @param {Object} lineData - Line data object
 * @returns {Number} Number of lines (minimum 1)
 */
const getNumberOfLines = (lineData) => {
  const numberOfLines = get(lineData, 'numberOfLines');
  const parsed = parseInt(numberOfLines, 10);
  
  // If numberOfLines is not provided, empty, or invalid, default to 1
  if (!numberOfLines || Number.isNaN(parsed) || parsed < 1) {
    return 1;
  }
  
  return parsed;
};

/**
 * Calculate plan price based on order type, term, and plan
 * @param {Object} lineData - Line data object
 * @returns {Number} Plan price
 */
const calculatePlanPrice = (lineData) => {
  const orderType = get(lineData, 'orderType');
  const term = get(lineData, 'term');
  const plan = get(lineData, 'plan');
  const customPrice = get(lineData, 'customPrice');

  // If custom price is provided, use it
  if (customPrice) {
    return parseFloat(customPrice);
  }

  // Pricing data structure from linedetailsformlogic.png
  const pricingData = {
    'Forborne': {
      '36 Months': {
        'Office Phone Basic': 25,
        'Office Phone': 35,
        'Office Phone Plus': 50
      },
      'Month to Month': {
        'Office Phone Basic': 30,
        'Office Phone': 40,
        'Office Phone Plus': 50 // Custom price dropdown
      }
    },
    'Non-Forborne / Tariffed': {
      'Month to Month': {
        'Office Phone Basic': 66,
        'Office Phone': 75,
        'Office Phone Plus': 85
      }
    }
  };

  return get(pricingData, [orderType, term, plan], 0);
};

/**
 * Calculate toll free number price based on plan and selection
 * @param {Object} lineData - Line data object
 * @returns {Number} Toll free price
 */
const calculateTollFreePrice = (lineData) => {
  const plan = get(lineData, 'plan');
  const tollFreeSelection = get(lineData, 'tollFreeNumber');

  // Pricing data from linedetailsformlogic.png
  const tollFreePricing = {
    'Office Phone Basic': {
      'Yes': 2.95,
      'No': 0
    },
    'Office Phone': {
      'Yes': 0,
      'No': 0
    },
    'Office Phone Plus': {
      'Yes': 0,
      'No': 0
    }
  };

  return get(tollFreePricing, [plan, tollFreeSelection], 0);
};

/**
 * Get calling features description
 * @param {Object} lineData - Line data object
 * @returns {String} Calling features description
 */
const getCallingFeaturesDescription = (lineData) => {
  const features = [];

  // 1 Feature (for Office Phone Basic)
  const callingFeature1 = get(lineData, 'callingFeature1');
  if (callingFeature1) {
    features.push(callingFeature1);
  }

  // Unlimited features (for Office Phone and Office Phone Plus)
  const callingFeatureUnlimited = get(lineData, 'callingFeatureUnlimited', []);
  if (callingFeatureUnlimited && callingFeatureUnlimited.length > 0) {
    features.push(...callingFeatureUnlimited);
  }

  return features.length > 0 ? features.join(', ') : null;
};

/**
 * Get Office Phone Lines summary details for a single line
 * @param {Object} lineData - Single office phone line data
 * @returns {Array} Array of row objects for summary
 */
export const getOfficePhoneLinesSummaryDetails = (lineData) => {
  if (!lineData) {
    return [];
  }

  const rows = [];

  // Order Type
  if (get(lineData, 'orderType')) {
    rows.push({
      label: 'Order Type',
      description: get(lineData, 'orderType')
    });
  }

  // Term
  if (get(lineData, 'term')) {
    rows.push({
      label: 'Term',
      description: get(lineData, 'term')
    });
  }

  // Plan
  if (get(lineData, 'plan')) {
    const planPrice = calculatePlanPrice(lineData);
    const numberOfLines = getNumberOfLines(lineData);
    const totalPlanPrice = planPrice * numberOfLines;
    
    rows.push({
      qty: numberOfLines,
      label: 'Plan',
      description: get(lineData, 'plan'),
      monthlyCharges: totalPlanPrice,
      oneTimeCharges: 0,
      fiveYearEasyPaymentCharges: 0,
      threeYearEasyPaymentCharges: 0
    });
  }

  // Custom Price (if applicable)
  if (get(lineData, 'customPrice')) {
    const customPriceValue = parseFloat(get(lineData, 'customPrice', 0));
    const planPrice = calculatePlanPrice(lineData);
    const numberOfLines = getNumberOfLines(lineData);
    const additionalCharges = (customPriceValue - planPrice) * numberOfLines;
    
    if (additionalCharges > 0) {
      rows.push({
        qty: numberOfLines,
        label: 'Custom Price',
        description: `$${customPriceValue}`,
        monthlyCharges: additionalCharges,
        oneTimeCharges: 0,
        fiveYearEasyPaymentCharges: 0,
        threeYearEasyPaymentCharges: 0
      });
    }
  }

  // Customer Type
  // if (get(lineData, 'customerType')) {
  //   rows.push({
  //     label: 'Customer Type',
  //     description: get(lineData, 'customerType'),
  //     monthlyCharges: 0,
  //     oneTimeCharges: 0,
  //     fiveYearEasyPaymentCharges: 0,
  //     threeYearEasyPaymentCharges: 0
  //   });
  // }

rows.push({
  label: 'Set Up Info',
  rows: [
    ...(get(lineData, 'lineDesignation')
      ? [
          {
            description: `Line Designation: ${get(lineData, 'lineDesignation')}`
          }
        ]
      : []),
    ...(get(lineData, 'lineType')
      ? [
          {
            description: `Line Type: ${get(lineData, 'lineType')}`
          }
        ]
      : [])
  ]
});

  // Line Designation
  // if (get(lineData, 'lineDesignation')) {
  //   rows.push({
  //     label: 'Line Designation',
  //     description: get(lineData, 'lineDesignation'),
  //     monthlyCharges: 0,
  //     oneTimeCharges: 0,
  //     fiveYearEasyPaymentCharges: 0,
  //     threeYearEasyPaymentCharges: 0
  //   });
  // }

  // Line Type
  // if (get(lineData, 'lineType')) {
  //   rows.push({
  //     label: 'Line Type',
  //     description: get(lineData, 'lineType'),
  //     monthlyCharges: 0,
  //     oneTimeCharges: 0,
  //     fiveYearEasyPaymentCharges: 0,
  //     threeYearEasyPaymentCharges: 0
  //   });
  // }

  // Number of Lines (if applicable)
  const numberOfLines = getNumberOfLines(lineData);
  if (numberOfLines > 1) {
    rows.push({
      qty: numberOfLines,
      label: 'Number of Lines',
      description: numberOfLines.toString(),
      monthlyCharges: 0,
      oneTimeCharges: 0,
      fiveYearEasyPaymentCharges: 0,
      threeYearEasyPaymentCharges: 0
    });
  }

  // Phone Numbers (if applicable)
  if (get(lineData, 'phoneNumbers')) {
    rows.push({
      label: 'Phone Numbers',
      description: get(lineData, 'phoneNumbers'),
      monthlyCharges: 0,
      oneTimeCharges: 0,
      fiveYearEasyPaymentCharges: 0,
      threeYearEasyPaymentCharges: 0
    });
  }

  // Calling Features
  const callingFeatures = getCallingFeaturesDescription(lineData);
  if (callingFeatures) {
    rows.push({
      label: 'Calling Features',
      description: callingFeatures
    });
  }

  // Toll Free Number
  if (get(lineData, 'tollFreeNumber')) {
    const tollFreePrice = calculateTollFreePrice(lineData);
    rows.push({
      qty: 1, // Always qty 1 for toll free number
      label: 'Toll Free Number',
      description: get(lineData, 'tollFreeNumber'),
      monthlyCharges: tollFreePrice,
      oneTimeCharges: 0,
      fiveYearEasyPaymentCharges: 0,
      threeYearEasyPaymentCharges: 0
    });
  }

  // Add Ons
  const addOns = get(lineData, 'addOns', []);
  if (addOns && addOns.length > 0) {
    const numberOfQuantity = getNumberOfLines(lineData);
    const plan = get(lineData, 'plan');
    
    const addOnRowData = { label: 'Add Ons', monthlyCharges: 0, rows: [] };
    
    addOns.forEach(addOn => {
      const addOnPrice = calculateAddOnPrice(addOn, plan);
      const addOnDescription = addOnPrice > 0 ? addOn : `${addOn} (Included)`;
      
      const row = {
        qty: numberOfQuantity,
        description: addOnDescription,
        price: addOnPrice,
        monthlyCharges: addOnPrice * numberOfQuantity,
        oneTimeCharges: 0,
        fiveYearEasyPaymentCharges: 0,
        threeYearEasyPaymentCharges: 0
      };
      
      addOnRowData.monthlyCharges += addOnPrice * numberOfQuantity;
      addOnRowData.rows.push(row);
    });
    
    rows.push(addOnRowData);
  }

  return rows;
};
